import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type TypeWorkInfo } from '../../types';
import { TypeTimeLineItem } from '~/hooks';

export const initialWorkInfoState: TypeWorkInfo = {
  status: null,
  center: null,
  cluster: null,
  workPart: null,
  teamName: '',
  workShift: '',
  overWorkWishedStatus: null,
  checkInOutStatus: null,
  endWorkAvailableFrom: '',
  qrCode: null,
  recentCheckInOut: null,
  workType: null,
  lateMinutes: null,
};

type TypeWorkInfoStore = {
  workInfo: TypeWorkInfo;
  todayWorkTimeline: TypeTimeLineItem[] | null;
  actions: {
    setWorkInfo: (workInfo: Partial<TypeWorkInfo>) => void;
    setTodayWorkTimeline: (todayWorkTimeline: TypeTimeLineItem[] | null) => void;
    resetWorkInfo: () => void;
    /** (공통) 출근 상태인지 여부를 반환 */
    getIsCommute: () => boolean;
    /** (공통) 연장 근무 희망 상태인지 여부를 반환 */
    getIsWishOverWork: () => boolean;
    /** (공통) 공정 체크인 상태인지 여부를 반환 */
    getIsCheckIn: () => boolean;
    /** (공통) 공정 체크아웃 상태인지 여부를 반환 */
    getIsCheckOut: () => boolean;
    /** (공통) 최근에 조퇴 진행함 상태인지 여부를 반환 */
    getIsEarlyEndWork: () => boolean;
    /** (공통) 퇴근 처리가 완료된 상태인지 여부를 반환 */
    getIsEndWork: () => boolean;
    /** (일용직) 계약 가능 상태인지 여부를 반환 */
    getIsContractable: () => boolean;
    /**
     * (공통) 출근 가능 상태인지 여부를 반환
     * - 일용직: 근로계약 후 출근은 하지 않은 경우
     * - 상용직: 사전등록 + 출근 가능한 근무계획이 존재하는 경우
     */
    getIsCommutable: () => boolean;
    /** (상용직) 근무 계획이 없는 상태인지 여부를 반환 */
    getIsContractEmptyWorkPlan: () => boolean;
    /** (상용직) 근무 계획은 있지만 휴무 근무유형인지 여부를 반환 */
    getIsContractDayOffWorkPlan: () => boolean;
    /** (상용직) 다음 근무계획이 근무시작가능 범위 밖에 있어 출근 불가능한 상태인지 여부를 반환 */
    getIsContractEndedWorkPlan: () => boolean;
  };
};

/** @description 상태를 로컬 스토리지에 저장하기 위한 키 */
const keyOfStorage = 'workInfo';

/** @description 출근, 체크인 정보를 관리하는 상태 */
export const useWorkInfoStore = create<TypeWorkInfoStore>()(
  persist(
    (set, get) => ({
      workInfo: initialWorkInfoState,
      todayWorkTimeline: null,
      actions: {
        setWorkInfo: (workInfo: Partial<TypeWorkInfo>) =>
          set((state) => ({
            ...state,
            workInfo: {
              ...state.workInfo,
              ...workInfo,
            },
          })),
        setTodayWorkTimeline: (todayWorkTimeline: TypeTimeLineItem[] | null) => set(() => ({ todayWorkTimeline })),
        resetWorkInfo: () => {
          set(() => ({ workInfo: initialWorkInfoState, todayWorkTimeline: null }));
          localStorage.removeItem(keyOfStorage);
        },
        getIsCommute: () => get().workInfo.status?.code === 'START_WORK',
        getIsContractable: () => get().workInfo.status?.code === 'AVAILABLE_LABOR_CONTRACT',
        getIsCommutable: () => get().workInfo.status?.code === 'AVAILABLE_START_WORK',
        getIsWishOverWork: () => get().workInfo.overWorkWishedStatus?.code === 'WISHED',
        getIsCheckIn: () => get().workInfo.checkInOutStatus?.code === 'CHECK_IN',
        getIsCheckOut: () => get().workInfo.checkInOutStatus?.code === 'CHECK_OUT',
        getIsEndWork: () => get().workInfo.status?.code === 'UNAVAILABLE_START_WORK_BY_TODAY_WORK_END',
        getIsEarlyEndWork: () => get().workInfo.status?.code === 'UNAVAILABLE_START_WORK_BY_INSUFFICIENT_REST',
        getIsContractEmptyWorkPlan: () => get().workInfo.status?.code === 'UNAVAILABLE_START_WORK_BY_MISSING_WORK_PLAN',
        getIsContractDayOffWorkPlan: () =>
          get().workInfo.status?.code === 'UNAVAILABLE_START_WORK_BY_DAY_OFF_WORK_PLAN',
        getIsContractEndedWorkPlan: () =>
          get().workInfo.status?.code === 'UNAVAILABLE_START_WORK_BY_TODAY_WORK_PLAN_ENDED',
      },
    }),
    {
      name: keyOfStorage,
      partialize: (state) => ({
        /** 업무 정보만 로컬 스토리지에 저장 */
        workInfo: state.workInfo,
      }),
    }
  )
);
